import { PrivacyChoicesButton } from "@withmethod/design/privacy";
import { useState } from "react";
import { BrandImage, Wordmark } from "@withmethod/design";
import { GoogleLogoIcon } from "@phosphor-icons/react";
import { api } from "./api";
import { saveCreateIntent } from "./create-intent";

const errors: Record<string, string> = {
  invalid_oauth_callback: "This sign-in request expired. Please try again.",
  sign_in_failed: "Sign-in did not finish. Please try again.",
  account_setup_failed:
    "We could not finish setting up your account. Please try again. If this continues, contact us.",
  organization_mismatch:
    "This sign-in is for a different workspace. Use your original account or contact us for help.",
  organization_access_required:
    "Your workspace access needs attention. If you received an invitation, use the link in that email. Otherwise, contact your workspace owner.",
  organization_selection_required:
    "Your account belongs to more than one workspace. Use work SSO to select a workspace, or contact us for help.",
  email_verification_required:
    "Verify your email with your sign-in provider, then try again.",
};

export function SignedOutScreen() {
  const [submitting, setSubmitting] = useState(false);
  const location = typeof window === "undefined" ? null : window.location;
  const params = new URLSearchParams(location?.search);
  const error = params.has("auth_error")
    ? (errors[params.get("auth_error")!] ?? errors.sign_in_failed)
    : null;
  const rememberIntent = () => {
    try {
      saveCreateIntent(
        window.sessionStorage,
        window.location.pathname,
        window.location.search,
      );
    } catch {
      /* Sign-in remains available without storage. */
    }
  };
  return (
    <div className="method-login">
      <header>
        <a
          href="https://withmethod.ai"
          className="app-wordmark"
          aria-label="Method home"
        >
          <Wordmark />
        </a>
      </header>
      <main>
        <section className="auth-form" aria-labelledby="login-title">
          <h1 id="login-title">Sign in to Method</h1>
          <p>Build, run, and improve your workflows.</p>
          {error && (
            <div className="login-error" role="alert">
              {error} <a href="mailto:ryan@withmethod.ai">Contact Method</a>
            </div>
          )}
          <form
            action={api.signInUrl()}
            method="get"
            onSubmit={() => {
              rememberIntent();
              setSubmitting(true);
            }}
          >
            <input type="hidden" name="provider" value="google" />
            <button className="auth-google" type="submit" disabled={submitting}>
              <GoogleLogoIcon size={20} weight="bold" aria-hidden="true" />
              {submitting ? "Opening Google…" : "Continue with Google"}
            </button>
          </form>
          <a
            className="auth-sso"
            href={api.signInUrl()}
            onClick={rememberIntent}
          >
            Use work SSO
          </a>
          <p className="auth-legal">
            By continuing, you agree to our{" "}
            <a
              href="https://withmethod.ai/terms/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{" "}
            and acknowledge our{" "}
            <a
              href="https://withmethod.ai/privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
        </section>
      </main>
      <figure className="login-art">
        <BrandImage kind="workshop" loading="eager" />
      </figure>
      <footer>
        <span>© {new Date().getFullYear()} Method</span>
        <PrivacyChoicesButton />
        <a href="https://withmethod.ai">Back to Method</a>
      </footer>
    </div>
  );
}
